/**
 * sessionResume — Persistance de l'état Owen avant un checkout Stripe
 * =====================================================================
 *
 * Stocke un "snapshot" minimal dans `sessionStorage` AVANT toute redirection
 * Stripe (popup OU full-page). Au retour sur `/payment-success?type=recharge`,
 * la page de succès lit ce snapshot pour repositionner l'utilisateur EXACTEMENT
 * sur la page d'échange en cours, sans casser le fil de conversation.
 *
 * Pourquoi `sessionStorage` ?
 *   • Survit aux full-page redirects Stripe.
 *   • Isolé par onglet — pas de fuite multi-tabs.
 *   • Auto-effacé à la fermeture de l'onglet (pas de pollution long-terme).
 *
 * Schéma du snapshot :
 *   {
 *     reason: 'credits_exhausted_checkout',
 *     pathname: '/coach-vocal/12',
 *     search: '?free=1',
 *     mode: 'libre' | 'certifiant' | 'module',
 *     moduleId: '12' | null,
 *     objectiveId: 'obj_xxx' | null,
 *     sessionId: 'sess_xxx' | null,
 *     timestamp: 1700000000000,
 *   }
 *
 * TTL implicite : 30 minutes (résilience contre un checkout abandonné).
 */

const KEY = 'coach_vocal_resume_state';
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

export function saveResumeState(snapshot) {
  try {
    const payload = {
      reason: 'credits_exhausted_checkout',
      pathname: snapshot.pathname || window.location.pathname,
      search: snapshot.search || window.location.search || '',
      mode: snapshot.mode || null,
      moduleId: snapshot.moduleId ?? null,
      objectiveId: snapshot.objectiveId ?? null,
      sessionId: snapshot.sessionId ?? null,
      timestamp: Date.now(),
    };
    window.sessionStorage.setItem(KEY, JSON.stringify(payload));
    return true;
  } catch (_) {
    return false;
  }
}

export function loadResumeState() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.pathname || !parsed?.timestamp) return null;
    if (Date.now() - parsed.timestamp > MAX_AGE_MS) {
      clearResumeState();
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

export function clearResumeState() {
  try { window.sessionStorage.removeItem(KEY); } catch (_) { /* noop */ }
}

/** Reconstruit l'URL complète (pathname + search) pour `navigate()`. */
export function getResumeUrl(snapshot) {
  if (!snapshot?.pathname) return null;
  return `${snapshot.pathname}${snapshot.search || ''}`;
}
